import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { FileText, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function ResourceCard({ r }) {
  const cover = r.cover_image;
  return (
    <article
      className="aoa-card h-full flex flex-col"
      data-testid={`latest-resource-${r.id || r.slug || r.title}`}
    >
      {cover ? (
        <img
          src={cover}
          alt=""
          width={800}
          height={450}
          loading="lazy"
          decoding="async"
          className="aspect-[16/9] w-full rounded-xl object-cover border border-slate-200 mb-4 bg-[#F1F5F9]"
        />
      ) : (
        <div className="aspect-[16/9] w-full rounded-xl bg-gradient-to-br from-[hsl(var(--aoa-navy))] to-[hsl(var(--aoa-royal))] mb-4 grid place-items-center">
          <FileText className="h-10 w-10 text-white/85" />
        </div>
      )}
      {r.category && (
        <span className="inline-flex w-fit items-center rounded-full bg-[hsl(var(--aoa-royal))]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))]">
          {r.category}
        </span>
      )}
      <h3 className="mt-3 font-display text-lg font-semibold text-[hsl(var(--aoa-navy))] line-clamp-2">
        {r.title}
      </h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">
        {(r.description || "").slice(0, 140)}
        {r.description && r.description.length > 140 ? "…" : ""}
      </p>
      <a
        href={r.file_url || r.url || "#"}
        download={r.filename || undefined}
        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] hover:underline"
      >
        <Download className="h-3.5 w-3.5" /> View / Download
      </a>
    </article>
  );
}

export default function LatestPublicationsCarousel() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/resources", { params: { published: true, limit: 6, sort: "-published_at" } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  if (items === null) return null;

  if (items.length === 0) {
    return (
      <section className="bg-white" data-testid="latest-publications-empty">
        <div className="aoa-container py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl border border-dashed border-slate-200 bg-[hsl(var(--aoa-gray-bg))] p-10 text-center max-w-3xl mx-auto"
          >
            <span className="aoa-eyebrow justify-center">Resources & Publications</span>
            <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">Latest Publications</h2>
            <p className="mt-3 text-sm text-slate-600 max-w-xl mx-auto">
              New publications will appear here soon. Subscribe via the contact page to be notified.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  const autoplay = items.length >= 3;

  return (
    <section className="bg-white" data-testid="latest-publications-carousel">
      <div className="aoa-container py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8 max-w-2xl"
        >
          <span className="aoa-eyebrow">Resources & Publications</span>
          <h2 className="aoa-h2 mt-3 text-[hsl(var(--aoa-navy))]">Latest Publications</h2>
          <p className="mt-2 text-sm text-slate-600">
            Recently published documents from AOA Somalia.
          </p>
        </motion.div>

        <div className="relative">
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            autoplay={autoplay ? { delay: 6000, disableOnInteraction: false } : false}
            pagination={{ clickable: true, el: ".latest-pubs-pagination" }}
            navigation={{
              prevEl: ".latest-pubs-prev",
              nextEl: ".latest-pubs-next",
            }}
            className="!pb-2"
          >
            {items.map((r) => (
              <SwiperSlide key={r.id || r.title} className="h-auto">
                <ResourceCard r={r} />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="latest-pubs-pagination flex gap-1.5" />
            <div className="flex gap-2">
              <button
                className="latest-pubs-prev grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-[hsl(var(--aoa-navy))] hover:border-[hsl(var(--aoa-royal))] transition-colors"
                aria-label="Previous"
                data-testid="latest-pubs-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                className="latest-pubs-next grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-[hsl(var(--aoa-navy))] hover:border-[hsl(var(--aoa-royal))] transition-colors"
                aria-label="Next"
                data-testid="latest-pubs-next"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
